import { useId, useState } from 'react'
import { ChevronDown, Globe, Lock } from 'lucide-react'
import { MetaPhaseLink } from '@/components/MetaPhaseLink'

/**
 * Slim utility banner modeled on the USWDS site banner. Uses fixed neutral
 * slate colors (not theme-aware) to match the cross-protoware look and
 * because that's how government-grade utility bars read across both light
 * and dark surfaces.
 */
export function UtilityBanner() {
  const [open, setOpen] = useState(false)
  const panelId = useId()

  return (
    <div
      className="w-full border-b border-stone-200 bg-stone-50 text-[13px] text-stone-700"
      // colors locked — do not theme-swap
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex flex-wrap items-center gap-2 py-1.5">
          <span aria-hidden className="text-[15px] leading-none">
            🇺🇸
          </span>
          <span className="leading-tight">
            A secure web application and source for public geospatial data.
          </span>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls={panelId}
            className={[
              'ml-auto inline-flex items-center gap-1 rounded px-1 font-medium',
              // Link-like hover affordance so it reads as interactive at a glance.
              'text-stone-700 underline-offset-2 cursor-pointer',
              'hover:text-stone-950 hover:underline',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-500',
              'transition-colors',
            ].join(' ')}
          >
            Here&apos;s how you know
            <ChevronDown
              size={14}
              className={`transition-transform ${open ? 'rotate-180' : ''}`}
              aria-hidden
            />
          </button>
        </div>

        {open && (
          <div id={panelId} className="grid gap-4 pb-4 pt-2 sm:grid-cols-2">
            <div className="flex gap-3">
              <Globe
                size={26}
                aria-hidden
                className="mt-0.5 flex-shrink-0 text-stone-500"
              />
              <div>
                <p className="font-semibold text-stone-900">
                  Built by <MetaPhaseLink />
                </p>
                <p className="mt-0.5 leading-relaxed text-stone-500">
                  GeoBorder is built on public geographic data and ships as
                  open source. Source provenance and vintage travel with
                  every layer in the API response.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <Lock
                size={26}
                aria-hidden
                className="mt-0.5 flex-shrink-0 text-stone-500"
              />
              <div>
                <p className="font-semibold text-stone-900">
                  Secure <code className="font-[var(--font-mono)] text-[12px]">https://</code> connection
                </p>
                <p className="mt-0.5 leading-relaxed text-stone-500">
                  A lock icon or <code className="font-[var(--font-mono)] text-[12px]">https://</code>{' '}
                  means you&apos;re connected securely. Every GeoBorder page
                  and API endpoint requires HTTPS, with traffic encrypted
                  in transit using TLS.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
